import React from 'react'
import classNames from 'classnames'

/**
 * Component to mask sensitive content from PostHog and Localize.
 * By default, it masks both.
 *
 * @param children - The content to mask
 * @param className - The class name to apply to the component
 * @param as - The component to render the content as
 * @param maskPosthog - When true (default), blocks PostHog. Set to false to allow capture.
 * @param maskLocalize - When true (default), blocks Localize. Set to false to allow translation.
 * @param [key: string] - Additional props to pass to the component
 */
interface PhiMaskProps {
  children: React.ReactNode
  className?: string
  dataTestId?: string
  as?: React.ElementType
  /** When true (default), blocks PostHog. Set to false to allow capture. */
  maskPosthog?: boolean
  /** When true (default), blocks Localize. Set to false to allow translation. */
  maskLocalize?: boolean
  [key: string]: unknown
}

export function PhiMask({
  children,
  className = '',
  as: Component = 'div',
  maskPosthog = true, // Default to true (masking).
  maskLocalize = true, // Default to true (masking).
  ...props
}: PhiMaskProps) {
  // Apply 'ph-no-capture' if maskPosthog is true.
  const classNameToApply = classNames(
    maskPosthog && 'ph-no-capture',
    maskLocalize && 'notranslate',
    className,
  )

  const elementProps: Record<string, unknown> = {
    className: classNameToApply,
    // Add Localize-recognized masking attributes when requested.
    ...(maskLocalize ? { notranslate: '' as const, translate: 'no' as const } : {}),
    ...props,
  }

  return <Component {...elementProps}>{children}</Component>
}
